/**
 * "To buy" — the other half of the kitchen.
 *
 * On the Kitchen screen behind a segmented control rather than as a fifth tab.
 * The dock is already about 130px of permanent chrome and a fifth target makes
 * every one of them narrower; more to the point, "what I have" and "what I need"
 * are the two states food has relative to you, and they belong on one screen.
 *
 * The list has two halves, and the order matters. **On the list** is what you're
 * actually taking to the shop and comes first. **Suggestions** are the app's
 * opinion and sit below, one tap from being real — offered, never added, because
 * a list that grows by itself is one you stop trusting the moment it is wrong
 * once.
 *
 * Every line says where it came from. "Chorizo — Chorizo rice" is checkable;
 * "Chorizo" appearing unbidden is not.
 */

import { useState } from "react";
import type { FormEvent } from "react";

import type { CartLine } from "../features/cart/types";
import type { CartSuggestion } from "../features/cart/suggestCart";
import { Icon } from "./Icon";

import "./CartScreen.css";

interface CartScreenProps {
  lines: CartLine[];
  suggestions: CartSuggestion[];
  onAdd: (name: string, source: CartLine["source"], reason?: string) => void;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  onClearGot: () => void;
  /** Files the ticked lines as food in the house. */
  onStow: () => void;
}

const SOURCE_LABELS: Record<CartLine["source"], string> = {
  recipe: "for a recipe",
  low: "running low",
  typed: "added by you",
};

export function CartScreen({
  lines,
  suggestions,
  onAdd,
  onToggle,
  onRemove,
  onClearGot,
  onStow,
}: CartScreenProps) {
  const [typed, setTyped] = useState("");

  const got = lines.filter((line) => line.got);
  const todo = lines.filter((line) => !line.got);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = typed.trim();
    if (!name) return;
    onAdd(name, "typed");
    setTyped("");
  }

  return (
    <div className="cart">
      <form className="cart__add" onSubmit={submit}>
        <input
          className="cart__input"
          value={typed}
          placeholder="Add anything"
          aria-label="Add to the list"
          onChange={(event) => setTyped(event.target.value)}
        />
        <button className="cart__submit" type="submit" disabled={!typed.trim()}>
          <Icon name="plus" className="cart__submit-icon" />
          <span className="sr-only">Add</span>
        </button>
      </form>

      {lines.length === 0 && suggestions.length === 0 && (
        <p className="cart__empty">
          Nothing to buy. Cook something and whatever it needs will turn up here.
        </p>
      )}

      {todo.length > 0 && (
        <ul className="cart__list">
          {todo.map((line) => (
            <li key={line.id}>
              <div className="cart__line">
                <button
                  className="cart__tick"
                  type="button"
                  aria-pressed={line.got}
                  aria-label={`Got ${line.name}`}
                  onClick={() => onToggle(line.id)}
                >
                  <Icon name="check" className="cart__tick-icon" />
                </button>
                <span className="cart__text">
                  <span className="cart__name">{line.name}</span>
                  <span className="cart__why">{line.reason ?? SOURCE_LABELS[line.source]}</span>
                </span>
                <button
                  className="cart__remove"
                  type="button"
                  aria-label={`Remove ${line.name}`}
                  onClick={() => onRemove(line.id)}
                >
                  <Icon name="close" className="cart__remove-icon" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {got.length > 0 && (
        <section className="cart__got">
          {/*
            The other end of the loop.

            You ticked these off in the shop; now they are in the house, and
            typing them all in again is the same tax the receipt scanner exists
            to remove. "Clear" stays beside it for the things that were never
            food — a list holds bin bags too, and filing those in a fridge would
            be worse than not offering.
          */}
          <button className="cart__stow" type="button" onClick={onStow}>
            <Icon name="fridge" className="cart__stow-icon" />
            Move {got.length} to the kitchen
          </button>

          <div className="cart__got-head">
            <h2 className="cart__heading">In the basket</h2>
            <button className="cart__clear" type="button" onClick={onClearGot}>
              Clear {got.length}
            </button>
          </div>
          <ul className="cart__list">
            {got.map((line) => (
              <li key={line.id}>
                <div className="cart__line" data-got="true">
                  <button
                    className="cart__tick"
                    type="button"
                    aria-pressed={line.got}
                    aria-label={`Put ${line.name} back`}
                    onClick={() => onToggle(line.id)}
                  >
                    <Icon name="check" className="cart__tick-icon" />
                  </button>
                  <span className="cart__text">
                    <span className="cart__name">{line.name}</span>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {suggestions.length > 0 && (
        <section className="cart__suggested">
          <h2 className="cart__heading">Worth adding</h2>
          {/* Offered, never added. A list that grows by itself is one you stop
              trusting the first time it's wrong. */}
          <ul className="cart__chips">
            {suggestions.map((entry) => (
              <li key={`${entry.source}-${entry.name}`}>
                <button
                  className="cart__chip"
                  type="button"
                  onClick={() => onAdd(entry.name, entry.source, entry.reason)}
                >
                  <Icon name="plus" className="cart__chip-icon" />
                  {entry.name}
                  {entry.reason && <span className="cart__chip-why">{entry.reason}</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
